import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import posthog from 'posthog-js'
import { api } from '../lib/api'
import { oublierJeton } from '../lib/reseauNatif'

export type User = {
  id: string
  email: string
  name: string | null
  lastName: string | null
  birthDate: string | null
  avatarUrl: string | null
  phone: string | null
  city: string | null
  cityLat: number | null
  cityLng: number | null
  bio: string | null
  directoryVisible: boolean
  showLastName: boolean
  showCity: boolean
  showPhone: boolean
  notifyOnRsvp: boolean
  notifyOnMessage: boolean
  notifyReminderJ1: boolean
  notifyNewsletter: boolean
  notifyOnContactAdded: boolean
  onboardingCompletedAt: string | null
  subscriptionStatus: string | null
  isPioneer: boolean
  isOfficial?: boolean
  isAdmin?: boolean
  suspendedAt?: string | null
  suspendedUntil?: string | null
  suspensionReason?: string | null
  bannedAt?: string | null
  banReason?: string | null
  subscriptionEndsAt: string | null
}

type AuthCtx = {
  user: User | null
  loading: boolean
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AuthCtx>({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  async function refresh() {
    try {
      const data = await api<{ user: User | null }>('/auth/me')
      setUser(data.user)
      try {
        if ((posthog as any).__loaded && data.user) {
          posthog.identify(data.user.id, {
            email: data.user.email,
            name: data.user.name,
            subscriptionStatus: data.user.subscriptionStatus,
            isPioneer: data.user.isPioneer,
          })
        }
      } catch (err) {
        console.error('[posthog] identify on refresh failed', err)
      }
    } catch (e) {
      // Ne déconnecter que sur un vrai 401 (session invalide/expirée) — jamais sur un hoquet
      // réseau/5xx transitoire (réseau mobile instable), sinon l'utilisateur est éjecté « sans
      // raison ». api.ts attache err.status ; une erreur réseau n'a pas de status.
      if ((e as { status?: number })?.status === 401) setUser(null)
    } finally {
      setLoading(false)
    }
  }

  async function logout() {
    try {
      await api('/auth/logout', { method: 'POST' })
    } catch {}
    // 🔴 2026-09-10, trouvé en revue — SANS CETTE LIGNE, LA DÉCONNEXION NE DÉCONNECTE PAS
    // dans la coquille native. Le jeton de session est un HMAC **sans état**, valable
    // 30 jours, que le serveur ne peut pas révoquer : `/auth/logout` se contente
    // d'effacer le cookie, ce qui ne concerne que le web. En natif, `setUser(null)`
    // affichait un écran déconnecté pendant que le jeton restait rangé — au redémarrage
    // de l'app, `refresh()` le présentait de nouveau, obtenait un 200 et **remettait la
    // personne dans son compte**. Sur un téléphone prêté ou partagé, le suivant héritait
    // du compte. Sans effet sur le web (aucun jeton n'y est jamais rangé).
    oublierJeton()
    try {
      if ((posthog as any).__loaded) posthog.reset()
    } catch (err) {
      console.error('[posthog] reset failed', err)
    }
    setUser(null)
  }

  useEffect(() => {
    refresh()
  }, [])

  return <Ctx.Provider value={{ user, loading, refresh, logout }}>{children}</Ctx.Provider>
}

export function useAuth() {
  return useContext(Ctx)
}
